import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import {
  ConnectionList,
  ConnectionState,
  ConnectionStatus,
  Provider,
} from '../../api-clients/oem-connection';
import { CspViolationService } from '../../services/csp-violation.service';
import { classifyOemHttpError } from '../../services/http-error-classifier';
import { OemIntegrationService } from '../../services/oem-integration.service';

interface UnmappedCount {
  provider: Provider;
  label: string;
  count: number;
}

interface ProviderRow {
  provider: Provider;
  label: string;
  status: ConnectionStatus;
  /** Computed UI status - mirrors the state matrix in the Task 9 brief. */
  uiStatus: 'not-connected' | 'connected' | 'verification-needed' | 'disconnected' | 'token-expiring';
  statusChip: string;
  primaryAction: 'connect' | 'manage';
  primaryLabel: string;
  /** True when there is at least one Deere org in pending/revoked state. Deere only. */
  hasPendingGrants: boolean;
}

// Exported for reuse by other OEM-linkage surfaces (SRA003-449/-457).
export const PROVIDER_LABELS: Record<Provider, string> = {
  [Provider.Deere]: 'John Deere',
  [Provider.Cnh]: 'Case New Holland',
};

// Fixed SRA application labels for the connected-state chip. Deliberately
// decoupled from the vendor-returned oem_org_name (which reflects the
// connecting account's CNH/Deere company, e.g. "Clevvi", not SRA). Per Aland's
// decision the chip shows the SRA application name, not the live vendor org.
export const CONNECTED_CHIP: Record<Provider, string> = {
  [Provider.Deere]: 'Connected to Sugar Research Australia John Deere Application',
  [Provider.Cnh]: 'Connected to Sugar Research Australia Case New Holland Application',
};

// Maps the documented OAuth callback error reasons (per the BE callback
// handlers) to user-friendly toast messages. Any reason key not present here
// falls back to a generic "unknown error" message via handleQueryParams, so a
// future BE-side reason addition still produces a visible toast (no silent
// failure).
const OAUTH_ERROR_REASONS: Record<string, string> = {
  missing_params: 'OAuth provider rejected the authorisation request',
  invalid_state: 'security check failed - please try again',
  token_exchange_failed: 'OAuth provider rejected the token exchange',
  no_org: 'no organisations returned',
  org_lookup_failed: 'organisation lookup error - try Retry verification',
  orgs_call_failed: 'organisation lookup returned an unexpected response',
};

@Component({
    selector: 'app-integrations',
    templateUrl: './integrations.component.html',
    styleUrls: ['./integrations.component.css'],
    standalone: false
})
export class IntegrationsComponent implements OnInit, OnDestroy {
  loading = false;
  rows: ProviderRow[] = [];
  loadError = false;
  loadErrorMessage = '';
  /**
   * Per-provider unmapped-events counter banner inputs (SRA003-116). Computed
   * from the response's `recent[]` so we get a per-provider split without a
   * second BE query — accurate for the recent window (default 10), which is
   * sufficient to drive a "you have unmapped events here" nudge.
   */
  unmappedCounts: UnmappedCount[] = [];
  /** When set, ManageView is shown for this provider. */
  managingProvider: Provider | null = null;

  private querySub?: Subscription;
  private refreshSub?: Subscription;

  // Expose Provider enum to template.
  readonly Provider = Provider;

  constructor(
    private oemSvc: OemIntegrationService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private cspViolation: CspViolationService,
  ) {}

  ngOnInit(): void {
    this.querySub = this.route.queryParamMap.subscribe((params) => {
      const handled = this.handleQueryParams(params);
      this.loadConnections();
      this.loadUnmappedEvents();
      if (handled) {
        // Strip query params so a refresh does not re-toast.
        this.router.navigate(['/integrations'], { replaceUrl: true });
      }
    });
    this.refreshSub = this.oemSvc.connectionsRefresh$.subscribe(() => {
      this.loadConnections();
      this.loadUnmappedEvents();
    });
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
    this.refreshSub?.unsubscribe();
  }

  // ---------------- data load ----------------

  loadConnections(): void {
    this.loading = true;
    this.loadError = false;
    this.loadErrorMessage = '';
    this.oemSvc.listConnections().subscribe({
      next: (list) => {
        this.rows = this.toRows(list);
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        const classification = classifyOemHttpError(err, {
          recentCspViolation: this.cspViolation.recentViolation(),
        });
        this.toastr.error(classification.toast);
        console.warn('[oem-integration] loadConnections failed', {
          class: classification.class,
          logToken: classification.logToken,
          status: err?.status,
        });
        this.rows = [];
        this.loadError = true;
        this.loadErrorMessage = classification.toast;
        this.loading = false;
      },
    });
  }

  loadUnmappedEvents(): void {
    this.oemSvc.listUnmappedEvents().subscribe({
      next: (res) => {
        const recent = res?.recent ?? [];
        const byProvider = new Map<Provider, number>();
        for (const e of recent) {
          byProvider.set(e.provider, (byProvider.get(e.provider) ?? 0) + 1);
        }
        const next: UnmappedCount[] = [];
        byProvider.forEach((count, provider) => {
          next.push({ provider, label: PROVIDER_LABELS[provider], count });
        });
        this.unmappedCounts = next;
      },
      error: () => {
        // Banner is non-critical; silently drop on error.
        this.unmappedCounts = [];
      },
    });
  }

  private toRows(list: ConnectionList): ProviderRow[] {
    return [
      this.toRow(Provider.Deere, list?.deere),
      this.toRow(Provider.Cnh, list?.cnh),
    ];
  }

  private toRow(provider: Provider, status: ConnectionStatus | undefined): ProviderRow {
    const label = PROVIDER_LABELS[provider];
    const safeStatus: ConnectionStatus = status ?? { status: ConnectionState.NotConnected };

    let uiStatus: ProviderRow['uiStatus'] = 'not-connected';
    let statusChip = 'Not connected';
    let primaryAction: ProviderRow['primaryAction'] = 'connect';
    let primaryLabel = `+ Connect to ${label}`;

    switch (safeStatus.status) {
      case ConnectionState.Active: {
        const expiringSoon = this.isExpiringSoon(safeStatus.token_expires_at);
        if (expiringSoon) {
          uiStatus = 'token-expiring';
          statusChip = 'Token expiring soon';
        } else {
          uiStatus = 'connected';
          statusChip = CONNECTED_CHIP[provider];
        }
        primaryAction = 'manage';
        primaryLabel = 'Manage';
        break;
      }
      case ConnectionState.Error:
        uiStatus = 'verification-needed';
        statusChip = 'Verification needed';
        primaryAction = 'manage';
        primaryLabel = 'Manage';
        break;
      case ConnectionState.Expired:
        uiStatus = 'token-expiring';
        statusChip = 'Token expired';
        primaryAction = 'manage';
        primaryLabel = 'Manage';
        break;
      case ConnectionState.Revoked:
        uiStatus = 'disconnected';
        statusChip = 'Disconnected';
        primaryAction = 'connect';
        primaryLabel = `+ Connect to ${label}`;
        break;
      case ConnectionState.NotConnected:
      default:
        uiStatus = 'not-connected';
        statusChip = 'Not connected';
        primaryAction = 'connect';
        primaryLabel = `+ Connect to ${label}`;
        break;
    }

    const hasPendingGrants =
      provider === Provider.Deere && (safeStatus.orgs?.pending ?? 0) > 0;

    return {
      provider,
      label,
      status: safeStatus,
      uiStatus,
      statusChip,
      primaryAction,
      primaryLabel,
      hasPendingGrants,
    };
  }

  private isExpiringSoon(expiresAt: string | null | undefined): boolean {
    if (!expiresAt) {
      return false;
    }
    const expiresMs = Date.parse(expiresAt);
    if (Number.isNaN(expiresMs)) {
      return false;
    }
    // 30 min — Wave 3 narrows the chip to "genuinely about to expire" since
    // the new -139 OEM 401-refresh interceptor handles reactive refresh in
    // the happy path. Wave 1's 24h threshold tripped on every fresh
    // connection (Deere access tokens have ~1h TTL) so the chip was noise.
    const horizonMs = Date.now() + 30 * 60 * 1000;
    return expiresMs < horizonMs;
  }

  // ---------------- actions ----------------

  onPrimary(row: ProviderRow): void {
    if (row.primaryAction === 'connect') {
      this.connect(row.provider);
    } else {
      this.openManage(row.provider);
    }
  }

  connect(provider: Provider): void {
    this.oemSvc.startConnect(provider).subscribe({
      error: (err: HttpErrorResponse) => {
        const classification = classifyOemHttpError(err, {
          recentCspViolation: this.cspViolation.recentViolation(),
        });
        // SRA003-345: swap the classifier's "unknown" load-context toast for
        // connect-context copy so the user sees what they actually triggered.
        // Classifier stays pure; adapter lives at the call site.
        const toast =
          classification.class === 'unknown'
            ? `Could not start the connection to ${PROVIDER_LABELS[provider]}.`
            : classification.toast;
        this.toastr.error(toast);
        console.warn('[oem-integration] connect failed', {
          provider,
          class: classification.class,
          logToken: classification.logToken,
          status: err?.status,
        });
      },
    });
  }

  openManage(provider: Provider): void {
    this.managingProvider = provider;
  }

  /** Banner deep-link from the unmapped-events counter (SRA003-116). */
  onMapClicked(provider: Provider): void {
    this.openManage(provider);
  }

  closeManage(): void {
    this.managingProvider = null;
    this.loadConnections();
  }

  rowFor(provider: Provider): ProviderRow | undefined {
    return this.rows.find((r) => r.provider === provider);
  }

  // ---------------- query param surfacing ----------------

  /**
   * Returns true when handled (caller will strip the params from the URL).
   */
  private handleQueryParams(params: {
    get(name: string): string | null;
  }): boolean {
    const status = params.get('status');
    const reason = params.get('reason');
    const provider = params.get('provider');
    const grantStatus = params.get('grant_status');
    const oemOrgId = params.get('oem_org_id');

    let handled = false;

    if (status === 'connected' && provider) {
      this.toastr.success(`Connected to ${this.providerLabel(provider)}`);
      handled = true;
    } else if (status === 'error') {
      // Generic error branch — every documented reason key produces a toast,
      // and unknown reasons still fire a generic toast so we never silently
      // swallow a callback failure (SRA003-135).
      const detail =
        (reason && OAUTH_ERROR_REASONS[reason]) || 'unknown error';
      this.toastr.error(`Connection failed: ${detail}`);
      handled = true;
    }

    if (provider === Provider.Deere && grantStatus === 'granted' && oemOrgId) {
      this.toastr.success(`Data access granted for org ${oemOrgId}`);
      handled = true;
    }

    return handled;
  }

  private providerLabel(provider: string): string {
    if (provider === Provider.Deere) return PROVIDER_LABELS[Provider.Deere];
    if (provider === Provider.Cnh) return PROVIDER_LABELS[Provider.Cnh];
    return provider;
  }
}
